<template>
    <v-dialog v-model="dialog" persistent max-width="500">
        <v-card>
            <v-card-title class="pa-6">
                <v-icon class="mr-3" color="primary">mdi-lightning-bolt</v-icon>
                Create Quick Meeting
            </v-card-title>
            <v-divider />

            <v-card-text class="pa-6">
                <v-form ref="quickMeetingForm" v-model="formValid">
                    <v-text-field
                        v-model="meeting.name"
                        label="Title *"
                        outlined dense
                        :rules="[rules.required]"
                        prepend-inner-icon="mdi-format-title"
                        placeholder="Enter meeting title..."
                        class="mb-2"
                    />

                    <v-autocomplete
                        v-model="meeting.user_id"
                        :items="users"
                        item-text="name"
                        item-value="id"
                        label="Creator *"
                        outlined dense
                        :rules="[rules.required]"
                        prepend-inner-icon="mdi-account-circle"
                        placeholder="Select meeting creator..."
                        class="mb-2"
                    >
                        <template v-slot:selection="{ item }">
                            <v-chip small color="primary" text-color="white">
                                <v-avatar left class="mr-1">
                                    <span class="white--text text-caption">{{ getInitials(item.name) }}</span>
                                </v-avatar>
                                {{ item.name }}
                            </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-avatar>
                                <v-avatar :color="getAvatarColor(item.name)" size="32">
                                    <span class="white--text text-caption">{{ getInitials(item.name) }}</span>
                                </v-avatar>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ item.email || 'No email' }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>

                    <v-autocomplete
                        v-model="meeting.company_id"
                        :items="companies"
                        item-text="name"
                        item-value="id"
                        label="Company *"
                        outlined dense
                        :rules="[rules.required]"
                        prepend-inner-icon="mdi-domain"
                        placeholder="Select company..."
                        class="mb-2"
                    >
                        <template v-slot:selection="{ item }">
                            <v-chip small color="info" text-color="white">{{ item.name }}</v-chip>
                        </template>
                    </v-autocomplete>

                    <v-autocomplete
                        v-model="meeting.attendees"
                        :items="users"
                        item-text="name"
                        item-value="id"
                        label="Attendees"
                        outlined dense
                        multiple chips small-chips deletable-chips
                        prepend-inner-icon="mdi-account-multiple"
                        placeholder="Select attendees..."
                        class="mb-2"
                    >
                        <template v-slot:selection="{ item }">
                            <v-chip small :color="getAvatarColor(item.name)" dark close
                                @click:close="removeAttendee(item.id)">
                                <v-avatar left>
                                    <span class="white--text">{{ getInitials(item.name) }}</span>
                                </v-avatar>
                                {{ item.name }}
                            </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-avatar :color="getAvatarColor(item.name)">
                                <span class="white--text">{{ getInitials(item.name) }}</span>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ item.email || 'No email' }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>

                    <v-text-field
                        v-model="meeting.conference_link"
                        label="Meeting Link *"
                        outlined dense
                        :rules="[rules.required, rules.url]"
                        prepend-inner-icon="mdi-video"
                        placeholder="https://meet.google.com/..."
                        hint="Enter the conference URL (e.g. https://meet.google.com/xxx-xxxx-xxx)"
                        persistent-hint
                    />
                </v-form>
            </v-card-text>

            <v-divider />
            <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn text large @click="cancel">Cancel</v-btn>
                <v-btn color="primary" large :disabled="!formValid" :loading="creating" @click="submit">
                    <v-icon left>mdi-lightning-bolt</v-icon>
                    Create
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'QuickCreateMeetingDialog',
    props: {
        value: { type: Boolean, default: false },
        users: { type: Array, default: () => [] },
        companies: { type: Array, default: () => [] },
    },
    data() {
        return {
            creating: false,
            formValid: false,
            meeting: {
                name: null,
                user_id: null,
                company_id: null,
                conference_link: null,
                frequency: 'once',
                attendees: [],
            },
            rules: {
                required: value => !!value || 'This field is required',
                url: value => {
                    if (!value) return true;
                    const text = value.trim();
                    if (/^(https?:\/\/)[^\s]+$/i.test(text)) return true;
                    if (/https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}/i.test(text)) return true;
                    if (/https?:\/\/(?!tel\.meet)[^\s]+/i.test(text)) return true;
                    return 'Please enter a valid URL or paste a meeting invite containing a conference link';
                },
            },
        };
    },
    computed: {
        ...mapGetters({ get_user: 'auth/get_user' }),
        dialog: {
            get() { return this.value; },
            set(val) { this.$emit('input', val); },
        },
    },
    watch: {
        value(newVal) {
            if (newVal) this.initializeForm();
        },
    },
    methods: {
        initializeForm() {
            this.meeting = {
                name: null,
                user_id: this.get_user?.id || null,
                company_id: null,
                conference_link: null,
                frequency: 'once',
                attendees: [],
            };
            this.$nextTick(() => {
                this.$refs.quickMeetingForm?.resetValidation();
            });
        },
        submit() {
            if (!this.$refs.quickMeetingForm.validate()) return;
            this.meeting.conference_link = this.sanitizeConferenceLink(this.meeting.conference_link);
            this.$emit('create-meeting', { ...this.meeting });
            this.cancel();
        },
        cancel() {
            this.dialog = false;
            this.initializeForm();
        },
        sanitizeConferenceLink(link) {
            if (!link) return link;
            const text = link.trim();

            // Already a plain URL — strip query params/fragments
            try {
                const url = new URL(text);
                return url.origin + url.pathname.replace(/\/+$/, '');
            } catch { /* not a plain URL, fall through */ }

            // Pasted block — extract Google Meet link first (highest priority)
            const meetMatch = text.match(/https:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}/i);
            if (meetMatch) return meetMatch[0];

            // Fallback: any https URL that isn't tel.meet (phone dial-in)
            const urlMatch = text.match(/https?:\/\/(?!tel\.meet)[^\s]+/i);
            if (urlMatch) {
                try {
                    const url = new URL(urlMatch[0]);
                    return url.origin + url.pathname.replace(/\/+$/, '');
                } catch { return urlMatch[0]; }
            }

            return text;
        },
        removeAttendee(userId) {
            const index = this.meeting.attendees.indexOf(userId);
            if (index > -1) this.meeting.attendees.splice(index, 1);
        },
        getInitials(name) {
            if (!name) return 'U';
            return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
        },
        getAvatarColor(name) {
            const colors = ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info',
                'purple', 'pink', 'indigo', 'cyan', 'teal', 'green', 'orange', 'brown'];
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return colors[Math.abs(hash) % colors.length];
        },
    },
};
</script>

<style lang="scss" scoped>
.v-card {
    border-radius: 12px;

    .v-card-title {
        background-color: #f8f9fa;
        border-bottom: 1px solid #e9ecef;
    }
}
</style>
